import { z } from 'zod';

export interface SessionExportRowDto {
  customer_id: number;
  customer_name: string;
  session_date: string;
  session_count: number;
  biosensor_session_count: number;
}

export interface MemberTrendsRowDto {
  customer_id: number;
  customer_name: string;
  month_year: string;
  new_users: number;
  returning_users: number;
  total_users: number;
  return_user_rate: number;
}

export type ExportGroupBy = 'unit' | 'location' | 'contract';

const isValidIsoDateString = (val: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) return false;
  const [year, month, day] = val.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

export const exportQuerySchema = z.object({
  startDate: z
    .string()
    .optional()
    .refine(val => !val || isValidIsoDateString(val), {
      message: 'startDate must be in YYYY-MM-DD format',
    }),
  endDate: z
    .string()
    .optional()
    .refine(val => !val || isValidIsoDateString(val), {
      message: 'endDate must be in YYYY-MM-DD format',
    }),
  groupBy: z.enum(['unit', 'location', 'contract']).optional(),
  unitIds: z
    .string()
    .optional()
    .transform(val =>
      val
        ? val
            .split(',')
            .map(id => parseInt(id.trim(), 10))
            .filter(id => !isNaN(id))
        : undefined,
    ),
});

export interface ExportQueryDto {
  startDate?: string;
  endDate?: string;
  groupBy?: ExportGroupBy;
  unitIds?: string;
}

// Longest range, in calendar months (start and end month inclusive), one export may cover.
export const EXPORT_MAX_MONTHS = {
  daily: 3,
  monthly: 36,
} as const;

// Inclusive calendar-month span between two YYYY-MM-DD (or ISO) date strings.
export const countExportMonths = (startDate: string, endDate: string): number => {
  const [startYear, startMonth] = startDate.split('-').map(Number);
  const [endYear, endMonth] = endDate.split('-').map(Number);
  return (endYear - startYear) * 12 + (endMonth - startMonth) + 1;
};
